/* ──────────────────────────────────────────────────────────────────────────
 * Local operational store — browser-only persistence for the spine.
 *
 * The platform is frontend-only by decision, so this is where operational
 * history lives: localStorage, keyed per browser.
 *
 * ── WHAT THIS IS, AND WHAT IT IS NOT ──
 * It IS enough to make the workflow real in a demonstration: an incident
 * survives a refresh, the event log accumulates, approvals and timings
 * persist across a session, and a reviewer can walk the whole loop.
 *
 * It is NOT an audit trail, and the UI must never present it as one:
 *   - Anyone can edit it from devtools in ten seconds.
 *   - It is per-browser. Two officers do not share a picture; there is no
 *     multi-agency anything, because there is no shared state.
 *   - Clearing site data destroys it with no trace.
 *   - The hash chain below detects accidental corruption, not tampering —
 *     an attacker who edits a record can recompute every digest after it.
 *
 * Those are properties of the browser, not shortcomings of this code, and no
 * amount of frontend work removes them. Evidence that must survive scrutiny
 * needs a server. The design for one is kept in supabase/migrations/.
 * ────────────────────────────────────────────────────────────────────────── */

const KEY = "inqai.spine.v1";

const EMPTY = {
  incidents: {},
  events: [],
  decisions: [],
  approvals: [],
  tasks: [],
  observations: [],
  seq: 1,
};

/* A plain-data deep clone. structuredClone would be tidier but is absent
 * from CRA's jsdom test environment and from older browsers — and a store
 * that throws on first read takes the whole workflow down with it. EMPTY is
 * pure JSON, so this is equivalent here. */
const fresh = () => JSON.parse(JSON.stringify(EMPTY));

/* Every read and write is guarded. localStorage throws in private mode, when
 * the quota is exceeded, and when site data is blocked — none of which
 * should take the application down with it. A demo that degrades to
 * in-memory is better than one that shows a blank screen. */
let memoryFallback = null;

function read() {
  if (memoryFallback) return memoryFallback;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return fresh();
    const parsed = JSON.parse(raw);
    return { ...fresh(), ...parsed };
  } catch {
    // Corrupt or unavailable — start clean in memory rather than crash.
    memoryFallback = fresh();
    return memoryFallback;
  }
}

function write(state) {
  if (memoryFallback) { memoryFallback = state; return false; }
  try {
    window.localStorage.setItem(KEY, JSON.stringify(state));
    return true;
  } catch {
    memoryFallback = state;
    return false;
  }
}

export function isPersistent() {
  if (memoryFallback) return false;
  try {
    const probe = `${KEY}.probe`;
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

/* A cheap, synchronous digest. Not cryptographic — it cannot be, without
 * async SubtleCrypto — and it is labelled as such wherever it surfaces. It
 * catches accidental corruption and makes an obviously-edited log visible.
 * It does not resist deliberate tampering. */
export function chainDigest(prev, payload) {
  const input = `${prev || ""}|${JSON.stringify(payload)}`;
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < input.length; i += 1) {
    const c = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 + c, 0x85ebca6b) >>> 0;
  }
  return (h1.toString(16).padStart(8, "0") + h2.toString(16).padStart(8, "0"));
}

/* Recompute the chain and report the first break. Surfaced in the UI so the
 * log's integrity is visible rather than assumed. */
export function verifyChain(events) {
  let prev = null;
  for (const e of events) {
    const expected = chainDigest(prev, {
      incident_id: e.incident_id, kind: e.kind, to_status: e.to_status, at: e.at,
    });
    if (e.digest !== expected) {
      return { intact: false, brokenAt: e.id, reason: "Digest mismatch — the log was modified after it was written." };
    }
    prev = e.digest;
  }
  return { intact: true };
}

/* ── Accessors ────────────────────────────────────────────────────────── */
export function getState() { return read(); }

export function mutate(fn) {
  const state = read();
  const result = fn(state);
  const persisted = write(state);
  return { result, persisted };
}

export function nextId(state) {
  state.seq += 1;
  return state.seq;
}

export function clearStore() {
  memoryFallback = null;
  try { window.localStorage.removeItem(KEY); } catch { /* nothing to clear */ }
}

/* Export the whole operational record as JSON. With no server, this is the
 * only way an operator can take evidence out of the browser — and after a
 * demo it is the artefact worth keeping. */
export function exportRecord() {
  const s = read();
  return {
    exportedAt: new Date().toISOString(),
    source: "InQAi Kumbh — browser store",
    caveat:
      "Exported from browser localStorage. This is demonstration state, not " +
      "an audit record: it is per-browser, editable from devtools, and its " +
      "digests are non-cryptographic.",
    ...s,
  };
}
